import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { OwnerProfile } from '../../../types/owner';
import { useUpdateOwnerProfileMutation } from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import FormTextField from '../../../shared/FormTextField';
import Language from '@mui/icons-material/Language';
import Instagram from '@mui/icons-material/Instagram';
import Facebook from '@mui/icons-material/Facebook';
import LinkedIn from '@mui/icons-material/LinkedIn';
import YouTube from '@mui/icons-material/YouTube';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';

const validateHostname = (value: string, ...hosts: string[]) => {
    // URL.canParse(value) not available on our Node version
    try {
        const host = new URL(value).hostname.replace(/^www\./, '');
        return hosts.includes(host);
    } catch {
        return false;
    }
};

const Schema = z.object({
    websiteUrl: z.string().url().or(z.literal('')),
    instagramUrl: z
        .string()
        .url()
        .refine((value) => validateHostname(value, 'instagram.com'), {
            message: 'Invalid Instagram URL',
        })
        .or(z.literal('')),
    facebookUrl: z
        .string()
        .url()
        .refine((value) => validateHostname(value, 'facebook.com'), {
            message: 'Invalid Facebook URL',
        })
        .or(z.literal('')),
    linkedinUrl: z
        .string()
        .url()
        .refine((value) => validateHostname(value, 'linkedin.com'), {
            message: 'Invalid LinkedIn URL',
        })
        .or(z.literal('')),
    youtubeUrl: z
        .string()
        .url()
        .refine((value) => validateHostname(value, 'youtube.com', 'youtu.be'), {
            message: 'Invalid YouTube URL',
        })
        .or(z.literal('')),
});

const SocialIcon = ({
    href,
    label,
    children,
}: {
    href: string;
    label: string;
    children: React.ReactNode;
}) => {
    return (
        <IconButton
            href={href}
            aria-label={label}
            color='inherit'
            target='_blank'
            rel='noopener noreferrer'
            sx={{ '&:hover': { color: 'inherit' } }}
        >
            {children}
        </IconButton>
    );
};

const OwnerSocialLinksForm = ({
    owner,
    open,
    onClose,
}: {
    owner: OwnerProfile;
    open: boolean;
    onClose: () => void;
}) => {
    const [updateOwnerProfile] = useUpdateOwnerProfileMutation();

    const methods = useForm<z.infer<typeof Schema>>({
        defaultValues: {
            websiteUrl: '',
            instagramUrl: '',
            facebookUrl: '',
            linkedinUrl: '',
            youtubeUrl: '',
        },
        values: {
            websiteUrl: owner.websiteUrl ?? '',
            instagramUrl: owner.instagramUrl ?? '',
            facebookUrl: owner.facebookUrl ?? '',
            linkedinUrl: owner.linkedinUrl ?? '',
            youtubeUrl: owner.youtubeUrl ?? '',
        },
        resolver: zodResolver(Schema),
    });

    const { control, handleSubmit, reset } = methods;

    const handleClose = () => {
        onClose();
        reset();
    };

    const onSubmit = async (data: z.infer<typeof Schema>) => {
        try {
            const response = await updateOwnerProfile({
                ownerId: owner.id,
                ...data,
            }).unwrap();

            reset(response);
            toast.success('Social links updated successfully');
            onClose();
        } catch {
            toast.error('Error updating social links');
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xs'>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>Edit Social Links</DialogTitle>
                    <DialogContent>
                        <Stack spacing={1}>
                            <FormTextField
                                name='websiteUrl'
                                placeholder='Website'
                                control={control}
                                inputProps={{ 'aria-label': 'Website' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Language />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormTextField
                                name='instagramUrl'
                                placeholder='Instagram'
                                control={control}
                                inputProps={{ 'aria-label': 'Instagram' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Instagram />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormTextField
                                name='facebookUrl'
                                placeholder='Facebook'
                                control={control}
                                inputProps={{ 'aria-label': 'Facebook' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Facebook />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormTextField
                                name='linkedinUrl'
                                placeholder='LinkedIn'
                                control={control}
                                inputProps={{ 'aria-label': 'LinkedIn' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <LinkedIn />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormTextField
                                name='youtubeUrl'
                                placeholder='YouTube'
                                control={control}
                                inputProps={{ 'aria-label': 'YouTube' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <YouTube />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type='submit' variant='contained'>
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

const OwnerSocialLinks = ({
    owner,
    canEdit,
}: {
    owner: OwnerProfile;
    canEdit: boolean;
}) => {
    const [formOpen, setFormOpen] = useState(false);

    const hasLinks =
        owner.websiteUrl ||
        owner.instagramUrl ||
        owner.facebookUrl ||
        owner.linkedinUrl ||
        owner.youtubeUrl;

    return (
        <Stack direction='row' alignItems='center' sx={{ flexShrink: 0 }}>
            {hasLinks && (
                <Stack
                    direction='row'
                    component='ul'
                    spacing={1}
                    sx={{ listStyleType: 'none', m: 0, p: 0 }}
                >
                    {owner.websiteUrl && (
                        <li>
                            <SocialIcon
                                href={owner.websiteUrl}
                                label='Host Website'
                            >
                                <Language />
                            </SocialIcon>
                        </li>
                    )}
                    {owner.instagramUrl && (
                        <li>
                            <SocialIcon
                                href={owner.instagramUrl}
                                label='Instagram'
                            >
                                <Instagram />
                            </SocialIcon>
                        </li>
                    )}
                    {owner.facebookUrl && (
                        <li>
                            <SocialIcon
                                href={owner.facebookUrl}
                                label='Facebook'
                            >
                                <Facebook />
                            </SocialIcon>
                        </li>
                    )}
                    {owner.linkedinUrl && (
                        <li>
                            <SocialIcon
                                href={owner.linkedinUrl}
                                label='LinkedIn'
                            >
                                <LinkedIn />
                            </SocialIcon>
                        </li>
                    )}
                    {owner.youtubeUrl && (
                        <li>
                            <SocialIcon href={owner.youtubeUrl} label='YouTube'>
                                <YouTube />
                            </SocialIcon>
                        </li>
                    )}
                </Stack>
            )}
            {canEdit && (
                <>
                    {hasLinks ? (
                        <Tooltip title='Edit social links'>
                            <IconButton
                                aria-label='Edit social links'
                                onClick={() => setFormOpen(true)}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            startIcon={<Add />}
                            onClick={() => setFormOpen(true)}
                        >
                            Add social links
                        </Button>
                    )}
                    <OwnerSocialLinksForm
                        owner={owner}
                        open={formOpen}
                        onClose={() => setFormOpen(false)}
                    />
                </>
            )}
        </Stack>
    );
};

export default OwnerSocialLinks;
